// CloneFlow.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchFlowWithDetailsAsync } from '../../redux/flows/getFlowsSlice';
import { createTestFlowAsync } from '../../redux/flows/createFlowSlice';
import { createTestEventWithScenariosAsync } from '../../redux/flows/createEventSlice';
import { fetchAllFlows, filterFlowsByMarket } from '../../services/flowApiService';
import { selectMarket } from '../../redux/app/configSlice';
import { generateTempScenarioId, generateTempAttributeId } from '../../utilities/tempIdGenerator';
import { getMarketsAsync, selectMarkets } from '../../redux/markets/getMarketsSlice';

export default function CloneFlow() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentMarket = useSelector(selectMarket);
  const markets = useSelector(selectMarkets);

  const [open, setOpen] = useState(false);
  const [sourceMarket, setSourceMarket] = useState('');
  const [availableFlows, setAvailableFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState('');
  const [selectedFlowDetails, setSelectedFlowDetails] = useState(null);
  const [testCaseId, setTestCaseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [currentMarketFlows, setCurrentMarketFlows] = useState([]);

  const handleOpen = () => {
    dispatch(getMarketsAsync());
    setOpen(true);
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      // Reset form
      setSourceMarket('');
      setAvailableFlows([]);
      setSelectedFlow('');
      setSelectedFlowDetails(null);
      setTestCaseId('');
      setError('');
      setValidationError('');
    }
  };

  // Load flows for current market (for validation) - only when modal opens
  useEffect(() => {
    const loadCurrentMarketFlows = async () => {
      if (!open) return;

      try {
        // Get current market from localStorage (most reliable source)
        const storedMarket = localStorage.getItem('market');
        const market = storedMarket ? JSON.parse(storedMarket) : currentMarket;

        if (!market?.id) {
          console.warn('No market selected');
          return;
        }

        const allFlows = await fetchAllFlows();
        const filtered = filterFlowsByMarket(allFlows, market.id);
        setCurrentMarketFlows(filtered);
      } catch (error) {
        console.error('Error loading current market flows:', error);
      }
    };

    loadCurrentMarketFlows();
  }, [open, currentMarket]);

  // Load flows when source market changes
  useEffect(() => {
    const loadFlows = async () => {
      if (!sourceMarket) {
        setAvailableFlows([]);
        setSelectedFlow('');
        setSelectedFlowDetails(null);
        setTestCaseId('');
        return;
      }

      setLoadingFlows(true);
      setError('');

      try {
        const allFlows = await fetchAllFlows();
        const filtered = filterFlowsByMarket(allFlows, sourceMarket);
        setAvailableFlows(filtered);
        setSelectedFlow('');
        setSelectedFlowDetails(null);
        setTestCaseId('');
      } catch (err) {
        console.error('Error loading flows:', err);
        setError('Failed to load flows for selected market');
        setAvailableFlows([]);
      } finally {
        setLoadingFlows(false);
      }
    };

    loadFlows();
  }, [sourceMarket]);

  // Update flow details and test case ID when selected (no API call - use cached data)
  useEffect(() => {
    if (!selectedFlow) {
      setSelectedFlowDetails(null);
      setTestCaseId('');
      return;
    }

    // Find the selected flow from available flows (already loaded)
    const flowData = availableFlows.find((f) => f.flow_db_id === selectedFlow);
    if (flowData) {
      setSelectedFlowDetails(flowData);
      setTestCaseId(flowData.test_case_id || '');
    }
  }, [selectedFlow, availableFlows]);

  // Validate test case ID
  useEffect(() => {
    if (!testCaseId.trim()) {
      setValidationError('');
      return;
    }

    const isDuplicate = currentMarketFlows.some(
      (flow) => flow.test_case_id?.toLowerCase() === testCaseId.trim().toLowerCase(),
    );

    if (isDuplicate) {
      setValidationError(
        'This test case ID already exists in your current market. Please choose a different ID.',
      );
    } else {
      setValidationError('');
    }
  }, [testCaseId, currentMarketFlows]);

  const handleClone = async () => {
    if (!selectedFlowDetails) {
      setError('Please select a flow to clone');
      return;
    }

    if (!testCaseId.trim()) {
      setError('Please enter a test case ID');
      return;
    }

    if (validationError) {
      setError('Please fix validation errors before cloning');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Fetch full flow details with events/scenarios/attributes
      const fullFlowData = await dispatch(fetchFlowWithDetailsAsync(selectedFlow)).unwrap();

      // Step 2: Create the new flow
      const currentMarketId = currentMarket?.id;
      const createFlowResult = await dispatch(
        createTestFlowAsync({
          name: fullFlowData.flow_name,
          description: fullFlowData.flow_description || '',
          test_case_id: testCaseId.trim(),
          market: currentMarketId,
        }),
      );

      if (!createFlowResult.success || !createFlowResult.flowId) {
        throw new Error('Failed to create flow');
      }

      const newFlowId = createFlowResult.flowId;

      // Step 3: Clone all events with their scenarios and attributes
      const events = fullFlowData.events || [];

      for (let i = 0; i < events.length; i++) {
        const sourceEvent = events[i];

        // Prepare event data with new IDs
        const eventData = {
          name: sourceEvent.event_name,
          description: sourceEvent.description || '',
          order: sourceEvent.event_order,
        };

        // Prepare scenarios with new IDs
        const scenarios = (sourceEvent.scenarios || []).map((scenario) => {
          const newScenarioId = generateTempScenarioId();
          return {
            scenario_id: newScenarioId,
            name: scenario.scenario_name,
            type: scenario.scenario_type,
            description: scenario.description || '',
            matching_attribute_name: scenario.matching_attribute_name || null,
            matching_attribute_value: scenario.matching_attribute_value || null,
          };
        });

        // Prepare attributes with new IDs
        const attributes = [];
        (sourceEvent.scenarios || []).forEach((scenario, scenarioIndex) => {
          (scenario.scenario_attributes || []).forEach((attr) => {
            const newAttributeId = generateTempAttributeId();
            attributes.push({
              attribute_id: newAttributeId,
              scenarioIndex: scenarioIndex,
              name: attr.attribute_name,
              expectedValue: attr.expected_value,
              type: attr.attribute_type || 'Static',
            });
          });
        });

        // Create the event with all its nested data
        const createEventResult = await dispatch(
          createTestEventWithScenariosAsync(newFlowId, eventData, scenarios, attributes),
        );

        if (!createEventResult.success) {
          console.error(`Failed to create event ${i + 1}:`, createEventResult.error);
          throw new Error(`Failed to create event: ${sourceEvent.event_name}`);
        }
      }

      // Step 3: Navigate to the new flow
      // Trigger FlowList refresh so new flow appears when user navigates back
      window.dispatchEvent(new Event('flowUpdated'));

      // Close modal and navigate
      handleClose();
      navigate(`/flows/edit/${newFlowId}`);
    } catch (err) {
      console.error('Error cloning flow:', err);
      setError(err.message || 'Failed to clone flow. Please try again.');
      setLoading(false);
    }
  };

  const canClone =
    sourceMarket && selectedFlow && testCaseId.trim() && !validationError && !loading;

  return (
    <>
      <Button
        fullWidth
        variant="contained"
        startIcon={<ContentCopyIcon />}
        onClick={handleOpen}
        sx={{
          backgroundColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        Clone Test Case
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Test Case from Another Market</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Source Market</InputLabel>
            <Select
              value={sourceMarket}
              label="Source Market"
              onChange={(e) => setSourceMarket(e.target.value)}
              disabled={loading}
            >
              {markets.map((market) => (
                <MenuItem key={market.id} value={market.id}>
                  {market.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            fullWidth
            options={availableFlows}
            getOptionLabel={(option) => option.test_case_id || ''}
            value={availableFlows.find((f) => f.flow_db_id === selectedFlow) || null}
            onChange={(event, newValue) => {
              setSelectedFlow(newValue ? newValue.flow_db_id : '');
            }}
            disabled={!sourceMarket || loading || loadingFlows}
            loading={loadingFlows}
            noOptionsText={
              !sourceMarket ? 'Select a source market first' : 'No test cases available'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Test Case to Clone"
                placeholder="Search by test case ID..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingFlows ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2, p: 2, backgroundColor: 'custom.surfaceAlt', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Flow Name:
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {selectedFlowDetails?.flow_name || '-'}
            </Typography>

            <Typography variant="subtitle2" color="text.secondary">
              Description:
            </Typography>
            <Typography variant="body2">{selectedFlowDetails?.flow_description || '-'}</Typography>
          </Box>

          <TextField
            fullWidth
            label="Test Case ID"
            value={testCaseId}
            onChange={(e) => setTestCaseId(e.target.value)}
            disabled={!selectedFlow || loading}
            error={!!validationError}
            helperText={validationError || 'This will be the test case ID in your current market'}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <ContentCopyIcon />}
            onClick={handleClone}
            disabled={!canClone}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '&:disabled': {
                backgroundColor: 'action.disabledBackground',
              },
            }}
          >
            {loading ? 'Cloning...' : 'Clone Test Case'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
